'use client'

// React Imports
import { useState, useMemo } from 'react'

// MUI Imports
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'

// Third-party Imports
import { Icon } from '@iconify/react'
import { createColumnHelper } from '@tanstack/react-table'

// Component Imports
import PremiumModuleWrapper from '../../common/PremiumModuleWrapper'
import OptionMenu from '../../../../../components/common/Template_Layouts/@core/components/option-menu'

const columnHelper = createColumnHelper()

const CreditNoteListTable = ({
    data,
    loading,
    permissions,
    onAddClick,
    onShowClick,
    onEditClick,
    onCancelClick,
    onDeleteClick,
    onRequestApprovalClick,
    totalRecords,
    currentPage,
    itemsPerPage,
    onPageChange,
    onStatusChange,
    searchTerm
}) => {
    // ── Permission flags — mirrors objPermId mapping + statusActionMenuHandler ──
    // Default true while permissions are still loading (null) so UI isn't broken
    const canAdd    = permissions ? permissions.Add    !== false : true;
    const canEdit   = permissions ? permissions.Edit   !== false : true;
    const canView   = permissions ? permissions.View   !== false : true;
    const canDelete = permissions ? permissions.Delete !== false : true;

    const columns = useMemo(() => [
        columnHelper.accessor('CreatedAt', {
            header: 'CREATED ON',
            cell: ({ row }) => (
                <Typography sx={{ color: 'text.primary', fontWeight: 500 }}>
                    {row.original.CreatedAt}
                </Typography>
            )
        }),
        columnHelper.accessor('CreditNoteNumber', {
            header: 'CREDIT NOTE #',
            cell: ({ row }) => (
                <Typography
                    sx={{
                        color: 'primary.main',
                        fontWeight: 600,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline', cursor: 'pointer' }
                    }}
                    onClick={() => onShowClick && onShowClick(row.original)}
                >
                    {row.original.CreditNoteNumber}
                </Typography>
            )
        }),
        columnHelper.accessor('InvoiceNumber', {
            header: 'INVOICE #',
            cell: ({ row }) => (
                <Typography sx={{ color: 'text.secondary' }}>
                    {row.original.InvoiceNumber || '-'}
                </Typography>
            )
        }),
        columnHelper.accessor('ContactName', {
            header: 'CUSTOMER NAME',
            cell: ({ row }) => (
                <Typography sx={{ color: 'text.primary', fontWeight: 500 }}>
                    {row.original.ContactName}
                </Typography>
            )
        }),
        columnHelper.accessor('Amount', {
            header: 'AMOUNT',
            cell: ({ row }) => (
                <Typography sx={{ color: 'text.primary', fontWeight: 600 }}>
                    ₹ {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(row.original.Amount)}
                </Typography>
            )
        }),
        columnHelper.accessor('IssuedAt', {
            header: 'ISSUE DATE',
            cell: ({ row }) => (
                <Typography sx={{ color: 'text.secondary' }}>
                    {row.original.IssuedAt}
                </Typography>
            )
        }),
        columnHelper.accessor('Status', {
            header: 'STATUS',
            cell: ({ row }) => {
                const status = row.original.Status?.toLowerCase();
                let color = 'primary';
                if (status === 'open') color = 'primary';
                else if (['sent', 'approved'].includes(status)) color = 'success';
                else if (['cancelled', 'rejected'].includes(status)) color = 'error';
                else if (status === 'sent for approval' || status === 'request for approval') color = 'info';
                else if (status === 'partially approved') color = 'warning';

                return (
                    <Chip
                        label={row.original.Status}
                        color={color}
                        variant='tonal'
                        size='small'
                        sx={{ fontWeight: 500, textTransform: 'capitalize' }}
                    />
                );
            }
        }),
        columnHelper.accessor('action', {
            header: 'ACTION',
            cell: ({ row }) => {
                const cn = row.original;
                const status = cn.Status?.toLowerCase();

                // ── Mirror statusActionMenuHandler switch-case ─────────────────
                let options = [];

                // Show — controlled by canView (objPermId.View = ".btnInvoiceShow")
                const showOption = canView ? {
                    text: 'Show',
                    icon: <Icon icon='tabler:eye' fontSize={20} />,
                    menuItemProps: { onClick: () => onShowClick && onShowClick(cn) }
                } : null;

                // Request for Approval — controlled by canAdd (objPermId.Add = "#btnNewInvoice, #btnGenerateInvoice")
                const approvalOption = (cn.HasApprovalSetting && canAdd) ? {
                    text: 'Request for Approval',
                    icon: <Icon icon='tabler:send' fontSize={20} />,
                    menuItemProps: { onClick: () => onRequestApprovalClick && onRequestApprovalClick(cn) }
                } : null;

                // Edit — controlled by canEdit (objPermId.Edit = ".btnInvoiceEdit, .btnInvoiceRefund")
                const editOption = canEdit ? {
                    text: 'Edit',
                    icon: <Icon icon='tabler:edit' fontSize={20} />,
                    menuItemProps: { onClick: () => onEditClick && onEditClick(cn) }
                } : null;

                // Refund — controlled by canEdit
                const refundOption = canEdit ? {
                    text: 'Refund',
                    icon: <Icon icon='tabler:refresh' fontSize={20} />,
                    menuItemProps: { onClick: () => onEditClick && onEditClick(cn) }
                } : null;

                // Cancel — controlled by canDelete (objPermId.Delete = ".btnInvoiceDelete, .btnInvoiceCancel")
                const cancelOption = canDelete ? {
                    text: 'Cancel',
                    icon: <Icon icon='tabler:x' fontSize={20} style={{ color: '#ef4444' }} />,
                    menuItemProps: { onClick: () => onCancelClick && onCancelClick(cn), className: 'text-danger' }
                } : null;

                // Delete — controlled by canDelete
                const deleteOption = canDelete ? {
                    text: 'Delete',
                    icon: <Icon icon='tabler:trash' fontSize={20} style={{ color: '#ef4444' }} />,
                    menuItemProps: { onClick: () => onDeleteClick && onDeleteClick(cn), className: 'text-danger' }
                } : null;

                // Switch on status — exact mirror of statusActionMenuHandler
                let normalised = status;
                if (normalised === 'send for approved') normalised = 'sent for approval';

                switch (normalised) {
                    case 'open':
                    case 'edit':
                        options = [approvalOption, showOption, editOption, cancelOption, deleteOption].filter(Boolean);
                        break;
                    case 'approved':
                        options = [showOption, refundOption].filter(Boolean);
                        break;
                    case 'rejected':
                    case 'declined':
                        options = [showOption, editOption, cancelOption, deleteOption].filter(Boolean);
                        break;
                    case 'partially approved':
                    case 'sent for approval':
                    case 'cancelled':
                    case 'refunded':
                    case 'sent':
                    default:
                        options = [showOption].filter(Boolean);
                        break;
                }

                return (
                    <OptionMenu
                        iconClassName='text-textSecondary'
                        options={options}
                        icon={<Icon icon='ri:more-2-fill' fontSize={22} />}
                    />
                );
            },
            enableSorting: false
        })
    ], [onShowClick, onEditClick, onCancelClick, onDeleteClick, onRequestApprovalClick, canAdd, canEdit, canView, canDelete]);

    return (
        <PremiumModuleWrapper
            moduleName="CreditNote"
            stats={{}} // Handled by separate Card component in index
            showCards={false}
            data={data}
            columns={columns}
            totalRecords={totalRecords}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange}
            globalFilter={searchTerm}
            loading={loading}
            onAddClick={onAddClick}
            hasFetched={true}
        />
    )
}

export default CreditNoteListTable
